import { store, type Mode } from "./store";
import { schedule } from "./scheduler";
import { buildQuestion } from "./quiz";
import { AudioPlayer } from "./audio";
import { el, clear, svgIcon, ICONS, crestSVG } from "./ui/components";
import { renderQuestion, renderEmpty, type Quality } from "./ui/card";
import { openStats } from "./ui/stats";
import { openSettings } from "./ui/settings";
import { renderLogin } from "./ui/login";
import { checkAuth, pullState, scheduleSync, logout, flushSync } from "./sync";

const audio = new AudioPlayer();
const app = document.getElementById("app");
if (!app) throw new Error("#app missing");

const dueTile = el("div", { class: "v", text: "0" });
const newTile = el("div", { class: "v", text: "0" });
const doneTile = el("div", { class: "v", text: "0" });
const streakTile = el("div", { class: "v", text: "0" });

const segMastered = el("span", { class: "seg-mastered", style: { width: "0%" } });
const segLearning = el("span", { class: "seg-learning", style: { width: "0%" } });
const segNew = el("span", { class: "seg-new", style: { width: "0%" } });
const progressNum = el("div", { class: "num", text: "0%" });
const progressTitle = el("h3", { text: "FORTSCHRITT" });

const cardArea = el("div", { attrs: { id: "card-area" } });

const tabs: Array<{ mode: Mode; label: string }> = [
  { mode: "mc", label: "Auswahl" },
  { mode: "conj", label: "Konjugation" },
  { mode: "sentence", label: "Satz bauen" },
];

const tabButtons = new Map<Mode, HTMLButtonElement>();

function buildShell(): void {
  clear(app!);

  const crest = el("div", { class: "crest" });
  crest.appendChild(crestSVG());

  const titleBlock = el("div", { class: "title-block" }, [
    el("h1", { text: "Učimo hrvatski" }),
    el("div", { class: "sub", text: "Spaced Repetition" }),
  ]);

  const statsBtn = el(
    "button",
    {
      class: "icon-btn",
      attrs: { "aria-label": "Statistik öffnen" },
      on: { click: () => openStats(store) },
    },
    [svgIcon(ICONS.chart)]
  );

  const settingsBtn = el(
    "button",
    {
      class: "icon-btn",
      attrs: { "aria-label": "Einstellungen öffnen" },
      on: {
        click: () =>
          openSettings(store, {
            onChange: () => {
              updateChrome();
              showNext();
            },
            onReset: () => {
              updateChrome();
              showNext();
            },
          }),
      },
    },
    [svgIcon(ICONS.gear)]
  );

  const header = el("header", { class: "app-header" }, [crest, titleBlock, statsBtn, settingsBtn]);
  const checker = el("div", { class: "checker" });

  const statStrip = el("div", { class: "stat-strip" }, [
    el("div", { class: "stat-tile" }, [dueTile, el("div", { class: "l", text: "Fällig" })]),
    el("div", { class: "stat-tile" }, [newTile, el("div", { class: "l", text: "Neu" })]),
    el("div", { class: "stat-tile" }, [doneTile, el("div", { class: "l", text: "Heute" })]),
    el("div", { class: "stat-tile" }, [streakTile, el("div", { class: "l", text: "Serie" })]),
  ]);

  const tabsRow = el("div", { class: "tabs" });
  for (const { mode, label } of tabs) {
    const btn = el("button", {
      class: "tab",
      text: label,
      on: {
        click: () => {
          store.state.settings.mode = mode;
          store.save();
          syncTabActive();
          updateChrome();
          showNext();
        },
      },
    }) as HTMLButtonElement;
    tabButtons.set(mode, btn);
    tabsRow.appendChild(btn);
  }

  const bar = el("div", { class: "bar" }, [segMastered, segLearning, segNew]);
  const legend = el("div", { class: "legend" }, [
    el("span", {}, [el("span", { class: "dot mastered" }), document.createTextNode("Gefestigt")]),
    el("span", {}, [el("span", { class: "dot learning" }), document.createTextNode("Am Lernen")]),
    el("span", {}, [el("span", { class: "dot new" }), document.createTextNode("Neu")]),
  ]);
  const progressBlock = el("div", { class: "progress-block" }, [
    el("div", { class: "head" }, [progressTitle, progressNum]),
    bar,
    legend,
  ]);

  const wrap = el("div", { class: "wrap" }, [
    header,
    checker,
    statStrip,
    tabsRow,
    progressBlock,
    cardArea,
  ]);

  app!.appendChild(wrap);
}

function syncTabActive(): void {
  const mode = store.state.settings.mode;
  for (const [m, btn] of tabButtons.entries()) {
    btn.classList.toggle("active", m === mode);
  }
  const titles: Record<Mode, string> = {
    mc: "AUSWAHL · FORTSCHRITT",
    conj: "KONJUGATION · FORTSCHRITT",
    sentence: "SATZBAU · FORTSCHRITT",
  };
  progressTitle.textContent = titles[mode];
}

function updateChrome(): void {
  dueTile.textContent = String(store.getDueCount());
  newTile.textContent = String(store.getNewCount());
  doneTile.textContent = String(store.state.session.done);
  streakTile.textContent = String(store.state.session.streak);

  const g = store.globalStats();
  const total = g.total || 1;
  const m = (g.mastered / total) * 100;
  const l = (g.learning / total) * 100;
  const n = (g.new / total) * 100;
  segMastered.style.width = `${m}%`;
  segLearning.style.width = `${l}%`;
  segNew.style.width = `${n}%`;
  progressNum.textContent = `${Math.round(m)}%`;
  syncTabActive();
}

let currentCardId: string | null = null;

function showNext(): void {
  const card = store.pickNext();
  if (!card) {
    renderEmpty(cardArea, () => {
      store.bumpNewQuota(10);
      store.save();
      updateChrome();
      showNext();
    });
    currentCardId = null;
    return;
  }
  currentCardId = card.id;
  const q = buildQuestion(card, store.state.settings.dir);
  renderQuestion(cardArea, q, {
    audio,
    audioEnabled: store.state.settings.audio,
    autoAdvanceMs: store.state.settings.autoAdvanceMs,
    onResult: (correct: boolean) => {
      store.recordResult(card.id, correct);
      store.save();
      updateChrome();
    },
    onAdvance: (quality: Quality) => {
      const state = store.state.cards[card.id];
      if (state) {
        schedule(state, quality);
        store.save();
      }
      updateChrome();
      showNext();
    },
  });
}

async function bootstrap(): Promise<void> {
  const authed = await checkAuth();
  if (!authed) {
    renderLogin(app!, bootstrap);
    return;
  }
  await pullState();
  buildShell();
  syncTabActive();
  updateChrome();
  showNext();
  store.subscribe(scheduleSync);
}

bootstrap();
void currentCardId;
void flushSync;
void logout;
